import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import { toast } from 'react-toastify';
import EventBus from "../common/EventBus";
import { Table, Card, Form, Row, Col, Button, InputGroup, FormControl } from 'react-bootstrap';
import AdminService from "../services/admin.service";

const PKUsageSummary = ({ type }) => {
    const { user: currentUser } = useSelector((state) => state.auth);
    const [data, setData] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [periods, setPeriods] = useState([]);
    const [selectedPeriod, setSelectedPeriod] = useState("");
    const [searchName, setSearchName] = useState("");
    const [page, setPage] = useState(0);
    const pageSize = 50;

    useEffect(() => {
        if (currentUser && currentUser.roles.includes("ROLE_ADMIN")) {
            fetchPeriods();
        }
    }, [currentUser, type]);

    useEffect(() => {
        if (currentUser && currentUser.roles.includes("ROLE_ADMIN") && selectedPeriod) {
            handleSearch();
        }
    }, [selectedPeriod]);

    const fetchPeriods = () => {
        AdminService.getUsageSummaryPeriods(type).then(
            (response) => {
                setPeriods(response.data);
                if (response.data.length > 0) {
                    setSelectedPeriod(response.data[0]);
                }
            },
            (error) => {
                EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: `Failed to fetch ${type} periods: ` + (error.response?.data || error.message) });
            }
        );
    };

    const handleSearch = (e) => {
        if (e) e.preventDefault();
        setPage(0);
        fetchData(0, selectedPeriod, searchName);
    };

    const handleClearFilter = () => {
        setSearchName("");
        setPage(0);
        fetchData(0, selectedPeriod, "");
    };

    const fetchData = (targetPage, period, clientName) => {
        setLoading(true);
        AdminService.getUsageSummary(type, period, clientName, targetPage, pageSize).then(
            (response) => {
                const { data, totalCount } = response.data;
                setData(data || []);
                setTotalCount(totalCount || 0);
                setLoading(false);
            },
            (error) => {
                EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: `Failed to fetch ${type} usage summary: ` + (error.response?.data || error.message) });
                setLoading(false);
            }
        );
    };

    const handleNextPage = () => {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchData(nextPage, selectedPeriod, searchName);
    };

    const handlePrevPage = () => {
        const prevPage = Math.max(0, page - 1);
        setPage(prevPage);
        fetchData(prevPage, selectedPeriod, searchName);
    };

    if (!currentUser) {
        return <Navigate to="/login" />;
    }

    if (!currentUser.roles.includes("ROLE_ADMIN")) {
        return <div className="container-fluid py-4" style={{ background: 'var(--bg-gradient)', minHeight: 'calc(100vh - 76px)' }}><h3>Unauthorized</h3></div>;
    }

    const totalPages = Math.ceil(totalCount / pageSize);

    return (
        <div className="container-fluid py-4" style={{ background: 'var(--bg-gradient)', minHeight: 'calc(100vh - 76px)' }}>
            <Card className="shadow-sm">
                <Card.Header className="bg-dark text-white d-flex justify-content-between align-items-center">
                    <h4 className="mb-0">PK {type.charAt(0).toUpperCase() + type.slice(1).toLowerCase()} Usage Summary</h4>
                    <span className="badge bg-secondary">Total: {totalCount.toLocaleString()}</span>
                </Card.Header>
                <Card.Body>
                    <Form onSubmit={handleSearch}>
                        <Row className="mb-3 align-items-end">
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label>Select Period</Form.Label>
                                    <Form.Select 
                                        value={selectedPeriod} 
                                        onChange={(e) => setSelectedPeriod(e.target.value)}
                                        disabled={loading}
                                    >
                                        {periods.map(p => (
                                            <option key={p} value={p}>{p}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Search Client Name</Form.Label>
                                    <InputGroup>
                                        <FormControl 
                                            placeholder="Client name..."
                                            value={searchName}
                                            onChange={(e) => setSearchName(e.target.value)}
                                            disabled={loading}
                                        />
                                        <Button variant="primary" type="submit" disabled={loading}>
                                            {loading && <span className="spinner-border spinner-border-sm me-1"></span>}
                                            Search
                                        </Button>
                                        <Button 
                                            variant="outline-secondary" 
                                            onClick={handleClearFilter} 
                                            disabled={loading || !searchName}
                                        >
                                            Clear
                                        </Button>
                                    </InputGroup>
                                </Form.Group>
                            </Col>
                            <Col md={5} className="text-end">
                                <div className="d-flex justify-content-end align-items-center h-100 pb-1">
                                    <span className="me-3 text-muted">
                                        Page <strong>{page + 1}</strong> of {Math.max(1, totalPages)}
                                    </span>
                                    <Button 
                                        variant="outline-secondary" 
                                        size="sm" 
                                        className="me-2" 
                                        onClick={handlePrevPage} 
                                        disabled={page === 0 || loading}
                                    >
                                        Previous
                                    </Button>
                                    <Button 
                                        variant="outline-secondary" 
                                        size="sm" 
                                        onClick={handleNextPage} 
                                        disabled={page + 1 >= totalPages || loading}
                                    >
                                        Next
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </Form>

                    <Table striped bordered hover responsive>
                        <thead className="thead-dark">
                            <tr>
                                <th>Period</th>
                                <th>Client Name</th>
                                <th>Client ID</th>
                                <th>Event Type</th>
                                <th>Count</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="5" className="text-center">
                                        <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                                        Loading summary...
                                    </td>
                                </tr>
                            ) : data.length === 0 ? (
                                <tr><td colSpan="5" className="text-center">No records found for this period</td></tr>
                            ) : (
                                data.map((item, index) => (
                                    <tr key={`${item.period}-${item.client_id}-${item.type}-${index}`}>
                                        <td>{item.period}</td>
                                        <td>{item.client_name}</td>
                                        <td>{item.client_id}</td>
                                        <td>{item.type}</td>
                                        <td><strong>{item.count.toLocaleString()}</strong></td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>
        </div>
    );
};

export default PKUsageSummary;
